import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { tenantSlug, tenantLoginUrl } from '../utils/tenant';
import siteCss from './landing198x/styles.css?raw';

type Step = 'company' | 'credentials';

// 198X-themed login. Visual: the prototype's split-screen auth (dark brand panel +
// form) via the shared stylesheet. Logic is unchanged: workspace-first, auto-locks
// the workspace from the subdomain (estaie.198x.ai), real auth via useAuth().
export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('company');
  const [companyInput, setCompanyInput] = useState('');
  const [workspace, setWorkspace] = useState<{ name: string; slug: string } | null>(null);
  const [companyError, setCompanyError] = useState('');
  const [lockedToSubdomain, setLockedToSubdomain] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [credError, setCredError] = useState('');
  const [loading, setLoading] = useState(false);

  // estaie.198x.ai → resolve + lock the workspace, skip the company step.
  useEffect(() => {
    const slug = tenantSlug();
    if (!slug) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/v1/auth/workspace?name=${encodeURIComponent(slug)}`);
        if (res.ok) {
          setWorkspace(await res.json());
          setLockedToSubdomain(true);
          setStep('credentials');
        }
      } catch { /* fall back to the company step */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setCompanyError('');
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/auth/workspace?name=${encodeURIComponent(companyInput.trim())}`);
      if (!res.ok) { setCompanyError('Workspace not found. Check the company name.'); return; }
      const ws = await res.json();
      // On 198x.ai, hand off to the workspace subdomain to sign in (estaie → estaie.198x.ai).
      const redirect = tenantLoginUrl(ws.slug);
      if (redirect) { window.location.href = redirect; return; }
      setWorkspace(ws);
      setStep('credentials');
    } catch {
      setCompanyError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setCredError('');
    setLoading(true);
    try {
      const user = await login(email.trim(), password, workspace?.slug);
      navigate(user.role === 'super_admin' ? '/admin' : '/dashboard');
    } catch (err: any) {
      setCredError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const altLine = (
    <div className="auth-alt">Need an account? Contact your administrator. · <Link to="/">Home</Link></div>
  );

  return (
    <div className="x198">
      <style>{siteCss}</style>
      <div className="authgrid">
        {/* Brand panel (hidden on mobile by the shared CSS) */}
        <div className="authbrand">
          <div className="top"><div className="brandlogo">19<span className="r">8</span>X</div></div>
          <div className="mid">
            <div className="quote">Wake up to a pipeline that <em>filled itself</em> overnight.</div>
          </div>
          <div className="bot">
            <div className="pills"><span className="g">&#8734;</span> Continuity · Intelligence · Multiplication · Legacy</div>
          </div>
        </div>

        {/* Form */}
        <div className="authform">
          {step === 'company' ? (
            <form className="aform" onSubmit={handleCompany}>
              <div className="brandlogo aform-logo" style={{ marginBottom: 28 }}>19<span className="r">8</span>X</div>
              <div className="eyb">Sign in</div>
              <h2>Welcome back</h2>
              <div className="lead">Enter your company workspace to continue.</div>
              <div className="field">
                <label>Company</label>
                <input value={companyInput} onChange={(e) => setCompanyInput(e.target.value)}
                  required autoFocus placeholder="e.g. Acme Realty" />
              </div>
              {companyError && <div className="auth-err">{companyError}</div>}
              <button className="btnL" type="submit" disabled={loading || !companyInput.trim()}>
                {loading ? 'Checking…' : 'Continue →'}
              </button>
              {altLine}
            </form>
          ) : (
            <form className="aform" onSubmit={handleLogin}>
              <div className="brandlogo aform-logo" style={{ marginBottom: 28 }}>19<span className="r">8</span>X</div>
              <div className="eyb">Welcome back</div>
              <h2>Sign in</h2>
              <div className="lead">
                {lockedToSubdomain ? (
                  <>to <strong style={{ color: 'var(--pearl)' }}>{workspace?.name}</strong></>
                ) : (
                  <button type="button" className="auth-back" onClick={() => { setStep('company'); setCredError(''); }}>
                    <ArrowLeftIcon style={{ width: 14, height: 14 }} /> {workspace?.name}
                  </button>
                )}
              </div>
              <div className="field">
                <label>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoFocus placeholder="you@company.com" />
              </div>
              <div className="field">
                <label>Password</label>
                <div className="pwwrap">
                  <input type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
                  <button type="button" className="pweye" onClick={() => setShowPassword((s) => !s)} aria-label="Toggle password">
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              {credError && <div className="auth-err">{credError}</div>}
              <button className="btnL" type="submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in →'}
              </button>
              {altLine}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
